import {
  useSharePointFileBrowse,
  type SharePointBrowseRow,
} from "@/components/ExternalConnections/sharepoint-file-browse";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Column, Row } from "@/components/ui/flex";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadButton,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useExternalConnectionsData } from "@/data/use-external-connections-data";
import { smartDate } from "@/lib/date";
import {
  EXTERNAL_FILE_BATCH_LINK_MAX,
  type SelectedExternalItem,
} from "@/lib/external-file-browser";
import { externalFileControllerBrowseFiles } from "@/lib/gen-api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowLeft,
  faArrowUp,
  faArrowUpArrowDown,
  faFolder,
  faTrash,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { useDebounceCallback } from "usehooks-ts";
import { LoadingOverlay } from "../LoadingOverlay/LoadingOverlay";
import { MimeTypeIcon } from "../MimeTypeIcon";
import { Input } from "../ui/input";
import { ConnectSourceDialog } from "./ConnectSourceDialog";

type ExternalProvider = "GOOGLE_DRIVE" | "SHAREPOINT";

interface ExternalFileBrowserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupUuid: string;
  mode: "file" | "folder";
  onSelected: (items: SelectedExternalItem[]) => void;
  limit?: number;
  allowedMimeTypes?: string[];
}

type BrowseRow = SharePointBrowseRow;

export const ExternalFileBrowserDialog = ({
  open,
  onOpenChange,
  groupUuid,
  mode,
  onSelected,
  limit,
  allowedMimeTypes,
}: ExternalFileBrowserDialogProps) => {
  const { connections, deleteConnection, isDeleting } =
    useExternalConnectionsData(groupUuid);
  const [selectedConnection, setSelectedConnection] = useState<string | null>(
    null,
  );
  const [selectedProvider, setSelectedProvider] =
    useState<ExternalProvider | null>(null);
  const [folderPath, setFolderPath] = useState<
    { folderId: string; folderName: string }[]
  >([]);
  const [rows, setRows] = useState<BrowseRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(
    new Set(),
  );

  const clearFileSelection = useCallback(
    () => setSelectedFileIds(new Set()),
    [],
  );

  const sharePoint = useSharePointFileBrowse({
    onRowsChange: setRows,
    onLoadingChange: setLoading,
    onClearFileSelection: clearFileSelection,
  });
  const [sort, setSort] = useState<{
    column: "date" | "name";
    direction: "asc" | "desc";
  }>({
    column: "name",
    direction: "asc",
  });
  const [search, setSearch] = useState<string>("");

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      if (a.isFolder && !b.isFolder) return -1;
      if (!a.isFolder && b.isFolder) return 1;

      let compare = 0;
      const dateA = a.modifiedTime ? new Date(a.modifiedTime).getTime() : 0;
      const dateB = b.modifiedTime ? new Date(b.modifiedTime).getTime() : 0;
      switch (sort.column) {
        case "name":
          compare = a.name.localeCompare(b.name);
          break;
        case "date":
          compare = dateA - dateB;
          break;
      }
      return sort.direction === "asc" ? compare : -compare;
    });
  }, [rows, sort]);

  const loadFolderChildren = async (
    connectionUuid: string,
    driveId: string | undefined,
    folderId?: string,
    searchTerm?: string,
  ) => {
    setLoading(true);
    try {
      const response = await externalFileControllerBrowseFiles({
        path: { connectionUuid },
        query: {
          folderId: folderId ?? "",
          search: searchTerm ?? "",
          driveId: driveId ?? "",
        },
      });
      setRows(response.data?.files ?? []);
    } catch (error) {
      toast.error("Failed to load files");
      console.error("Failed to load files:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectionSelect = async (
    connectionUuid: string,
    provider: ExternalProvider,
  ) => {
    setSelectedConnection(connectionUuid);
    setSelectedProvider(provider);
    sharePoint.reset();
    setFolderPath([]);

    if (provider === "SHAREPOINT") {
      sharePoint.startConnection();
      return;
    }

    await loadFolderChildren(connectionUuid, undefined);
  };

  const handleNavigateIntoRow = async (row: BrowseRow) => {
    if (!selectedConnection || !row.isFolder) return;

    if (selectedProvider === "SHAREPOINT") {
      if (sharePoint.phase === "folder") {
        await sharePoint.navigateIntoFolder(selectedConnection, row, search);
        return;
      }
      await sharePoint.navigateIntoRow(selectedConnection, row);
      return;
    }

    clearFileSelection();
    setFolderPath((prev) => [
      ...prev,
      { folderId: row.id, folderName: row.name },
    ]);
    await loadFolderChildren(selectedConnection, undefined, row.id, search);
  };

  const toggleFileSelection = (row: BrowseRow) => {
    if (mode !== "file" || row.isFolder) return;

    if (limit === 1) {
      setSelectedFileIds(new Set([row.id]));
      return;
    }

    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(row.id)) {
        next.delete(row.id);
      } else if (next.size >= EXTERNAL_FILE_BATCH_LINK_MAX) {
        toast.error(
          `You can select at most ${EXTERNAL_FILE_BATCH_LINK_MAX} files at once`,
        );
        return prev;
      } else {
        next.add(row.id);
      }
      return next;
    });
  };

  const activeFolderPath =
    selectedProvider === "SHAREPOINT" ? sharePoint.folderPath : folderPath;
  const currentFolder =
    activeFolderPath.length > 0
      ? activeFolderPath[activeFolderPath.length - 1]
      : null;
  const activeDriveId =
    selectedProvider === "SHAREPOINT" ? sharePoint.selectedDriveId : null;

  const handleConfirm = () => {
    if (!selectedConnection) return;

    if (mode === "folder") {
      if (!currentFolder) return;
      onSelected([
        {
          kind: "folder",
          connectionUuid: selectedConnection,
          externalId: currentFolder.folderId,
          name: currentFolder.folderName,
          driveId: activeDriveId ?? undefined,
        },
      ]);
    } else {
      const selectedRows = rows.filter(
        (r) => !r.isFolder && selectedFileIds.has(r.id),
      );
      if (selectedRows.length === 0) return;

      onSelected(
        selectedRows.map((row) => ({
          kind: "file",
          mimeType: row.mimeType ?? "",
          connectionUuid: selectedConnection,
          externalId: row.id,
          name: row.name,
          driveId: activeDriveId ?? undefined,
        })),
      );
    }
    onOpenChange(false);
  };

  const isSharePointFolderPhase =
    selectedProvider === "SHAREPOINT" && sharePoint.isFolderPhase;
  const isSharePointPickerPhase =
    selectedProvider === "SHAREPOINT" && sharePoint.isPickerPhase;
  const isSharePointBrowseRootPhase =
    selectedProvider === "SHAREPOINT" && sharePoint.isBrowseRootPhase;
  const isConfirmDisabled =
    (mode === "folder" &&
      !isSharePointFolderPhase &&
      selectedProvider === "SHAREPOINT") ||
    (!currentFolder && selectedFileIds.size === 0) ||
    (limit === 1 && selectedFileIds.size > 1);
  const confirmLabel =
    mode === "folder"
      ? currentFolder
        ? `Select ${currentFolder.folderName}`
        : "Select Folder"
      : selectedFileIds.size <= 1
        ? "Add file"
        : `Add ${selectedFileIds.size} files`;

  const handleSort = (column: "date" | "name") => {
    setSort((prevSort) => ({
      column,
      direction:
        prevSort.column === column && prevSort.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const reloadCurrentView = useCallback(
    async (searchTerm: string) => {
      if (!selectedConnection) return;

      if (selectedProvider === "SHAREPOINT") {
        await sharePoint.reloadCurrentView(selectedConnection, searchTerm);
        return;
      }

      const currentFolderId =
        folderPath.length > 0
          ? folderPath[folderPath.length - 1].folderId
          : undefined;
      await loadFolderChildren(
        selectedConnection,
        undefined,
        currentFolderId,
        searchTerm,
      );
    },
    [selectedConnection, selectedProvider, folderPath, sharePoint],
  );

  const loadFilesDebounced = useCallback(
    useDebounceCallback((value: string) => {
      void reloadCurrentView(value);
    }, 500),
    [reloadCurrentView],
  );

  const handleSearch = (value: string) => {
    setSearch(value);
    loadFilesDebounced(value);
  };

  const activeConnections = connections.filter((c) => c.status === "active");

  const showFileTable =
    selectedConnection &&
    (selectedProvider !== "SHAREPOINT" || isSharePointFolderPhase);

  const tableColSpan =
    (showFileTable && mode === "file" ? 1 : 0) + 1 + (showFileTable ? 2 : 0);

  const handleBack = () => {
    if (!selectedConnection) return;

    if (selectedProvider === "SHAREPOINT") {
      void sharePoint.goBack(selectedConnection, search).then((disconnect) => {
        if (disconnect) {
          setSelectedConnection(null);
          setSelectedProvider(null);
        }
      });
      return;
    }

    if (folderPath.length > 0) {
      const newPath = folderPath.slice(0, -1);
      setFolderPath(newPath);
      clearFileSelection();
      const parentId =
        newPath.length > 0 ? newPath[newPath.length - 1].folderId : undefined;
      void loadFolderChildren(selectedConnection, undefined, parentId, search);
      return;
    }

    setSelectedConnection(null);
    setSelectedProvider(null);
    sharePoint.reset();
  };

  const pathLabel = () => {
    if (!selectedConnection) return null;
    if (selectedProvider === "SHAREPOINT") {
      if (sharePoint.phase === "browseRoot") {
        return "Choose location";
      }
      if (sharePoint.phase === "site") {
        return "Sites";
      }
      if (sharePoint.phase === "drive") {
        return (
          <>
            <span>{sharePoint.selectedSiteName}</span>
            <span>/</span>
            <span>Libraries</span>
          </>
        );
      }
      return (
        <>
          {sharePoint.browseRoot === "myFiles" ? (
            <span>My files</span>
          ) : (
            <>
              <span>{sharePoint.selectedSiteName}</span>
              <span>/</span>
              <span>{sharePoint.selectedDriveName}</span>
            </>
          )}
          {sharePoint.folderPath.map((f) => (
            <span key={f.folderId} className="flex items-center gap-1">
              <span>/</span>
              <span>{f.folderName}</span>
            </span>
          ))}
        </>
      );
    }
    return (
      <>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setFolderPath([]);
            setSelectedFileIds(new Set());
            void loadFolderChildren(
              selectedConnection,
              undefined,
              undefined,
              search,
            );
          }}
        >
          Root
        </Button>
        {folderPath.map((folder, index) => (
          <span key={folder.folderId} className="flex items-center gap-1">
            <span>/</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const newPath = folderPath.slice(0, index + 1);
                setFolderPath(newPath);
                setSelectedFileIds(new Set());
                void loadFolderChildren(
                  selectedConnection,
                  undefined,
                  folder.folderId,
                  search,
                );
              }}
            >
              {folder.folderName}
            </Button>
          </span>
        ))}
      </>
    );
  };

  return (
    <>
      <Dialog
        open={activeConnections.length > 0 && open}
        onOpenChange={onOpenChange}
      >
        <DialogContent className="max-h-[80vh] w-[70vw] sm:max-w-[70vw]">
          <DialogHeader>
            <DialogTitle>
              {mode === "folder"
                ? "Link cloud folder"
                : "Browse external files"}
            </DialogTitle>
            <DialogDescription>
              {mode === "folder"
                ? "Choose a connection, navigate into a folder, then select it to import the full folder tree into group knowledge."
                : "Choose a connection and select files to add to this folder. To import a whole folder tree, use Link Connector Folder from the kit menu."}
            </DialogDescription>
          </DialogHeader>

          <Column className="flex-1 gap-4 overflow-hidden">
            {!selectedConnection && (
              <Column className="gap-2">
                <div className="text-sm font-medium">Select a connection:</div>
                {activeConnections.length === 0 ? (
                  <div className="text-muted-foreground text-sm">
                    No active connections. Please connect an external source
                    first.
                  </div>
                ) : (
                  <Column className="gap-1">
                    {activeConnections.map((connection) => (
                      <div
                        key={connection.uuid}
                        className="flex items-center gap-2"
                      >
                        <Button
                          variant="outline"
                          onClick={() =>
                            handleConnectionSelect(
                              connection.uuid,
                              connection.provider as ExternalProvider,
                            )
                          }
                          className="flex-1 justify-start"
                          disabled={isDeleting}
                        >
                          <Row align="center" className="gap-2">
                            <span className="font-medium">
                              {connection.provider}
                            </span>
                            <span className="text-muted-foreground text-sm">
                              ({connection.user.email})
                            </span>
                          </Row>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="hover:bg-destructive hover:text-destructive-foreground mr-auto self-center"
                          onClick={() => deleteConnection(connection.uuid)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    ))}
                  </Column>
                )}
              </Column>
            )}

            {selectedConnection && isSharePointBrowseRootPhase && (
              <Column className="gap-3">
                <div className="text-sm font-medium">
                  Where do you want to browse?
                </div>
                <Row className="gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() =>
                      void sharePoint.selectBrowseRoot(
                        selectedConnection!,
                        "sites",
                      )
                    }
                    disabled={loading}
                  >
                    SharePoint sites
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() =>
                      void sharePoint.selectBrowseRoot(
                        selectedConnection!,
                        "myFiles",
                      )
                    }
                    disabled={loading}
                  >
                    My files
                  </Button>
                </Row>
              </Column>
            )}

            {selectedConnection && !isSharePointBrowseRootPhase && (
              <Column className="flex-1 gap-2 overflow-hidden">
                <div className="flex flex-wrap items-center gap-1 text-sm font-medium">
                  <div>Path:</div>
                  {pathLabel()}
                </div>
                {(showFileTable || isSharePointPickerPhase) && (
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder={
                        sharePoint.phase === "site"
                          ? "Search sites..."
                          : "Search by name..."
                      }
                      value={search}
                      disabled={loading}
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                    <Button variant="ghost" onClick={() => handleSearch("")}>
                      Clear
                    </Button>
                  </div>
                )}

                {mode === "folder" && isSharePointFolderPhase && (
                  <p className="text-muted-foreground text-sm">
                    Open at least one subfolder, then select it to link the
                    folder tree.
                  </p>
                )}

                <div className="flex-1 overflow-auto rounded-md border">
                  <Table className="table-fixed" stickyHeader>
                    <TableHeader sticky>
                      <TableRow>
                        {showFileTable && mode === "file" && limit !== 1 && (
                          <TableHead className="w-10" />
                        )}
                        <TableHead>
                          <TableHeadButton
                            onClick={() => handleSort("name")}
                            active={sort.column === "name"}
                          >
                            Name
                            <FontAwesomeIcon
                              icon={
                                sort.column === "name"
                                  ? sort.direction === "asc"
                                    ? faArrowUp
                                    : faArrowDown
                                  : faArrowUpArrowDown
                              }
                            />
                          </TableHeadButton>
                        </TableHead>
                        {showFileTable && (
                          <>
                            <TableHead className="w-35">
                              <TableHeadButton
                                onClick={() => handleSort("date")}
                                active={sort.column === "date"}
                              >
                                Date Modified
                                <FontAwesomeIcon
                                  icon={
                                    sort.column === "date"
                                      ? sort.direction === "asc"
                                        ? faArrowUp
                                        : faArrowDown
                                      : faArrowUpArrowDown
                                  }
                                />
                              </TableHeadButton>
                            </TableHead>
                            <TableHead className="w-30">Size</TableHead>
                          </>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={tableColSpan}
                            className="text-center"
                          >
                            No items found
                          </TableCell>
                        </TableRow>
                      ) : (
                        sortedRows.map((row) => {
                          const isFileRow = !row.isFolder;
                          const isDisabled =
                            (mode === "folder" && isFileRow) ||
                            (allowedMimeTypes &&
                              !allowedMimeTypes.includes(row.mimeType ?? ""));
                          const isFileSelected =
                            isFileRow && selectedFileIds.has(row.id);

                          return (
                            <TableRow
                              key={row.id}
                              className={`${isDisabled ? "opacity-50" : "cursor-pointer"} ${isFileSelected ? "bg-accent" : ""}`}
                              onClick={() => {
                                if (row.isFolder) {
                                  void handleNavigateIntoRow(row);
                                  return;
                                }
                                if (isDisabled) return;
                                toggleFileSelection(row);
                              }}
                            >
                              {showFileTable &&
                                mode === "file" &&
                                limit !== 1 && (
                                  <TableCell
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {isFileRow && (
                                      <Checkbox
                                        checked={isFileSelected}
                                        onCheckedChange={() =>
                                          toggleFileSelection(row)
                                        }
                                      />
                                    )}
                                  </TableCell>
                                )}
                              <TableCell>
                                <div className="flex flex-1 items-center gap-2 overflow-hidden">
                                  {row.isFolder ? (
                                    <FontAwesomeIcon icon={faFolder} />
                                  ) : (
                                    <MimeTypeIcon mimeType={row.mimeType} />
                                  )}
                                  <div className="truncate">{row.name}</div>
                                </div>
                              </TableCell>
                              {showFileTable && (
                                <>
                                  <TableCell className="text-muted-foreground text-sm">
                                    {row.modifiedTime
                                      ? smartDate(row.modifiedTime)
                                      : "-"}
                                  </TableCell>
                                  <TableCell className="text-muted-foreground text-sm">
                                    {row.size
                                      ? `${Math.round(row.size / 1024)} KB`
                                      : "-"}
                                  </TableCell>
                                </>
                              )}
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                  <LoadingOverlay loading={loading} title="Loading" />
                </div>
              </Column>
            )}
          </Column>

          <DialogFooter>
            {selectedConnection && (
              <Button
                variant="link"
                className="mr-auto self-center pl-0"
                onClick={handleBack}
              >
                <FontAwesomeIcon icon={faArrowLeft} /> Back
              </Button>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isConfirmDisabled}>
              {confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConnectSourceDialog
        open={activeConnections.length === 0 && open}
        onOpenChange={onOpenChange}
      />
    </>
  );
};
